import React from "react";
import { Tag } from "antd";
import { BulkOrderOperationType } from "../../../api/bulk-order-operations";

export interface OperationConfig {
	value: BulkOrderOperationType;
	/** Key inside this category's localization sub-namespace. */
	labelKey: string;
	descriptionKey: string;
	fallbackLabel: string;
	fallbackDescription: string;
	icon: React.ReactNode;
	/** Status ops accept an optional comment override; AddBalance does not. */
	supportsComment: boolean;
	/** Moves money — red accent + typed confirmation word. */
	danger?: boolean;
}

interface OperationCardsProps {
	operations: OperationConfig[];
	selected: BulkOrderOperationType | null;
	onSelect: (value: BulkOrderOperationType) => void;
	label: (op: OperationConfig) => string;
	description: (op: OperationConfig) => string;
	dangerTagText: string;
}

const OperationCards = ({
	operations,
	selected,
	onSelect,
	label,
	description,
	dangerTagText,
}: OperationCardsProps) => (
	<div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-3">
		{operations.map((op) => {
			const isSelected = selected === op.value;
			const accent = op.danger
				? isSelected
					? "border-red-500 bg-red-50"
					: "border-red-200 hover:border-red-400"
				: isSelected
				? "border-[#4F16C2] bg-[#4F16C2]/5"
				: "border-gray-200 hover:border-[#4F16C2]";

			return (
				<div
					key={op.value}
					role="button"
					tabIndex={0}
					aria-pressed={isSelected}
					className={`cursor-pointer rounded-lg border p-3 flex flex-col gap-1 transition-colors ${accent}`}
					onClick={() => onSelect(op.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							onSelect(op.value);
						}
					}}
				>
					<span
						className={`text-lg ${
							op.danger ? "text-red-500" : "text-[#4F16C2]"
						}`}
					>
						{op.icon}
					</span>
					<span className="font-medium flex items-center gap-2">
						{label(op)}
						{op.danger && <Tag color="red">{dangerTagText}</Tag>}
					</span>
					<span className="text-xs text-gray-500">
						{description(op)}
					</span>
				</div>
			);
		})}
	</div>
);

export default OperationCards;
